import { cprBandLabel } from '../../../Utils/PartnerHierarchy'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import {
  Box,
  Text,
  Flex,
  Link as ChakraLink,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge
} from '@chakra-ui/react'
import { Button, TableBox, Loading, money, get, labels, colors } from './shared'
import DiscountActions from '../../../components/TableDiscountsPartners/DiscountActions'

const ExpectedLabel = ({ row }) => (
  <>
    {row.expectedKind === 'RECONSTRUCTED' && (
      <Text fontSize="xs" color="gray.600">
        Reconstruido · v{row.assessmentVersion}
      </Text>
    )}
    {row.confidence && (
      <Badge
        mt={1}
        whiteSpace="normal"
        colorScheme={row.confidence === 'CONFIRMED' ? 'green' : 'orange'}
      >
        {
          {
            CONFIRMED: 'Confirmado',
            ESTIMATED: 'Estimado',
            REFERENCE: 'Último real',
            UNRESOLVED: 'Por determinar'
          }[row.confidence]
        }
      </Badge>
    )}
  </>
)
const ClassificationDetail = ({ row }) => (
  <>
    {row.contract && (
      <Text fontSize="xs">
        {row.contract} ·{' '}
        {row.contract === 'CPR'
          ? `Grado CPR: ${row.cprGrade ?? 'Sin definir'} · ${cprBandLabel(
              row.cprBand
            )}`
          : row.hierarchicalGroup || 'Sin grupo'}
        {row.hierarchicalGrade ? ` · ${row.hierarchicalGrade}` : ''}
      </Text>
    )}
    {row.reason && (
      <Text fontSize="xs" color="gray.600">
        {row.reason}
      </Text>
    )}
  </>
)

export const CollectionTable = ({ rows = [], showPeriod = false }) => (
  <TableBox>
    <Table size="sm">
      <Thead>
        <Tr>
          {showPeriod && <Th>Período</Th>}
          <Th>Socio</Th>
          <Th isNumeric>Esperado</Th>
          <Th isNumeric>Cobrado</Th>
          <Th isNumeric>
            Diferencia
            <br />
            (real − esperado)
          </Th>
          <Th>Resultado</Th>
          <Th>Detalle del descuento</Th>
        </Tr>
      </Thead>
      <Tbody>
        {!rows.length && (
          <Tr>
            <Td colSpan={showPeriod ? 7 : 6}>
              No hay registros para esta selección.
            </Td>
          </Tr>
        )}
        {rows.map((row, i) => (
          <Tr key={`${row.period || ''}-${row.rut}-${i}`}>
            {showPeriod && <Td whiteSpace="nowrap">{row.period}</Td>}
            <Td>
              <Text fontWeight="semibold">
                {row.partner ? (
                  <Link to={`/partners/profile/${row.partner}/Perfil de Socio`}>
                    {row.name}
                  </Link>
                ) : (
                  row.name
                )}
              </Text>
              <Text fontSize="xs" color="gray.600">
                {row.rut}
              </Text>
            </Td>
            <Td isNumeric>
              {money(row.expected)}
              <ExpectedLabel row={row} />
            </Td>
            <Td isNumeric>
              {row.actual ? money(row.actual.amount) : 'Sin respuesta'}
            </Td>
            <Td isNumeric>
              {row.difference == null ? '—' : money(row.difference)}
            </Td>
            <Td>
              <Badge colorScheme={colors[row.status]} whiteSpace="normal">
                {labels[row.status]}
              </Badge>
              {row.actual?.legacy && <Text fontSize="xs">Histórico</Text>}
            </Td>
            <Td>
              <Text fontSize="sm">{row.actual?.reason || '—'}</Text>
              <ClassificationDetail row={row} />
              {row.suggestedGrades?.length > 0 && (
                <Text fontSize="xs" color="blue.700">
                  Por monto: {row.suggestedGrades.join(', ')}. Pendiente de
                  confirmar.
                </Text>
              )}
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  </TableBox>
)

const periodLabel = (period) =>
  new Date(`${period}-15T12:00:00Z`).toLocaleDateString('es-CL', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  })
const sourceDateLabel = (value) => {
  if (!value) return null
  const date = String(value).replace(/-/g, '')
  if (!/^\d{8}(\d{6})?$/.test(date)) return 'Fecha no informada'
  return (
    `${date.slice(6, 8)}/${date.slice(4, 6)}/${date.slice(0, 4)}` +
    (date.length === 14
      ? ` ${date.slice(8, 10)}:${date.slice(10, 12)}:${date.slice(12, 14)}`
      : '')
  )
}
const uploadedLabel = (date) =>
  date &&
  new Date(date).toLocaleString('es-CL', { timeZone: 'America/Santiago' })
const responsiveRows = {
  '& th': { whiteSpace: 'normal' },
  '& td': { verticalAlign: 'top', overflowWrap: 'anywhere', py: 3 },
  '@media (max-width: 62em)': {
    '& thead': { display: 'none' },
    '& tbody, & tr, & td': { display: 'block', width: '100%' },
    '& tr': {
      border: '1px solid',
      borderColor: 'gray.200',
      borderRadius: 8,
      mb: 3
    },
    '& td': { border: 0, textAlign: 'left', py: 2 },
    '& td[data-label]::before': {
      content: 'attr(data-label)',
      display: 'block',
      fontWeight: 600,
      color: 'green.500',
      mb: 1
    }
  }
}

export function PartnerDiscountHistory({ partnerId, order = 'DESC' }) {
  const { user } = useSelector((state) => state.auth)
  const [page, setPage] = useState(0)
  const canArchive = user?.role === 'ADMINISTRADOR'
  const query = useQuery(
    ['billing', 'history', partnerId],
    () => get(`history/${partnerId}`),
    { enabled: Boolean(partnerId), retry: false }
  )
  useEffect(() => setPage(0), [partnerId, order])
  const rows = [...(query.data?.history || [])].sort((a, b) =>
    order === 'ASC'
      ? a.period.localeCompare(b.period)
      : b.period.localeCompare(a.period)
  )
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(rows.length / 10) - 1)
  )
  return (
    <Box minW={0} w="100%" mt={4}>
      <Text fontSize="sm" color="gray.600">
        El esperado proviene de la nómina guardada o de una reconstrucción
        identificada. Los grupos estimados requieren confirmación; una
        coincidencia de montos no acredita el grado. La diferencia es real menos
        esperado. La fecha de carga no reemplaza la fecha del descuento.
      </Text>
      <Loading query={query} />
      {query.isSuccess && (
        <>
          <TableBox>
            <Table
              size="sm"
              sx={responsiveRows}
              aria-label="Historial de descuentos"
            >
              <Thead>
                <Tr>
                  <Th>Fecha de descuento</Th>
                  <Th isNumeric>Esperado</Th>
                  <Th isNumeric>Descontado real</Th>
                  <Th isNumeric>
                    Diferencia
                    <br />
                    (real − esperado)
                  </Th>
                  <Th>Resultado</Th>
                  <Th>Detalle</Th>
                  {canArchive && <Th>Acciones</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {!rows.length && (
                  <Tr>
                    <Td colSpan={canArchive ? 7 : 6}>
                      No hay descuentos registrados para este socio.
                    </Td>
                  </Tr>
                )}
                {rows
                  .slice(currentPage * 10, (currentPage + 1) * 10)
                  .map((row) => (
                    <Tr key={row.period}>
                      <Td data-label="Fecha de descuento">
                        <Text fontWeight="semibold" textTransform="capitalize">
                          {periodLabel(row.period)}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {row.actual?.discountDate
                            ? sourceDateLabel(row.actual.discountDate)
                            : row.actual
                            ? 'Día no informado'
                            : 'Pendiente de respuesta'}
                        </Text>
                      </Td>
                      <Td data-label="Esperado" isNumeric>
                        {money(row.expected)}
                        <ExpectedLabel row={row} />
                      </Td>
                      <Td data-label="Descontado real" isNumeric>
                        {row.actual
                          ? money(row.actual.amount)
                          : 'Sin respuesta'}
                      </Td>
                      <Td data-label="Diferencia" isNumeric>
                        {row.difference == null ? '—' : money(row.difference)}
                      </Td>
                      <Td data-label="Resultado">
                        <Badge
                          colorScheme={colors[row.status]}
                          whiteSpace="normal"
                        >
                          {row.status === 'UNEXPECTED'
                            ? 'Cobrado sin esperado'
                            : labels[row.status]}
                        </Badge>
                      </Td>
                      <Td
                        data-label="Detalle"
                        maxW={{ base: 'none', lg: '240px' }}
                      >
                        <Text fontSize="sm">
                          {row.actual?.reason ||
                            row.actual?.legacyRecord?.observations ||
                            '—'}
                        </Text>
                        <ClassificationDetail row={row} />
                        {row.actual?.filename && (
                          <Text fontSize="xs" color="gray.600">
                            {row.actual.filename}
                          </Text>
                        )}
                        {row.actual?.responseDate && (
                          <Text fontSize="xs">
                            Respuesta:{' '}
                            {sourceDateLabel(row.actual.responseDate)}
                          </Text>
                        )}
                        {row.actual?.loadedAt && (
                          <Text fontSize="xs" color="gray.600">
                            Cargado: {uploadedLabel(row.actual.loadedAt)}
                          </Text>
                        )}
                        {row.actual?.legacyRecord?.support?.map((file) => (
                          <ChakraLink
                            key={file.url}
                            href={file.url}
                            isExternal
                            color="green.500"
                            fontSize="sm"
                            display="block"
                          >
                            {file.name}
                          </ChakraLink>
                        ))}
                      </Td>
                      {canArchive && (
                        <Td data-label="Acciones">
                          {row.actual?.legacyRecord?._id ? (
                            <DiscountActions
                              discount={row.actual.legacyRecord}
                            />
                          ) : (
                            '—'
                          )}
                        </Td>
                      )}
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </TableBox>
          {rows.length > 0 && (
            <Flex
              mt={3}
              gap={3}
              align="center"
              justify="space-between"
              wrap="wrap"
            >
              <Text fontSize="sm">
                {rows.length} meses · Página {currentPage + 1}
              </Text>
              <Flex gap={2}>
                <Button
                  size="sm"
                  isDisabled={!currentPage}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  isDisabled={(currentPage + 1) * 10 >= rows.length}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Siguiente
                </Button>
              </Flex>
            </Flex>
          )}
          {query.data?.revisions?.length > 0 && (
            <Accordion allowToggle mt={5}>
              <AccordionItem borderColor="gray.200">
                <AccordionButton color="green.500" fontWeight="semibold">
                  <Box flex={1} textAlign="left">
                    Archivos y correcciones
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={0}>
                  <TableBox>
                    <Table
                      size="sm"
                      sx={responsiveRows}
                      aria-label="Archivos y correcciones de descuentos"
                    >
                      <Thead>
                        <Tr>
                          <Th>Período</Th>
                          <Th>Archivo</Th>
                          <Th isNumeric>Descontado real</Th>
                          <Th>Resultado / corrección</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {query.data.revisions.map((r) => (
                          <Tr key={r._id}>
                            <Td data-label="Período">
                              {periodLabel(r.period)}
                            </Td>
                            <Td data-label="Archivo">
                              {r.source?.filename}
                              {r.responseDate && (
                                <Text fontSize="xs">
                                  Respuesta: {sourceDateLabel(r.responseDate)}
                                </Text>
                              )}
                              {r.source?.appliedAt && (
                                <Text fontSize="xs">
                                  Cargado: {uploadedLabel(r.source.appliedAt)}
                                </Text>
                              )}
                            </Td>
                            <Td data-label="Descontado real" isNumeric>
                              {money(r.amount)}
                            </Td>
                            <Td data-label="Resultado / corrección">
                              {r.outcome === 'REJECTED'
                                ? 'No cobrado'
                                : 'Aplicado'}
                              <Text fontSize="xs">
                                {r.reason || r.source?.correctionReason}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableBox>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
        </>
      )}
    </Box>
  )
}
